import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  berekenDuurInFrames,
  duw,
  kanOngedaanMaken,
  kanOpnieuwDoen,
  kloonProject,
  maakHistorie,
  ongedaanMaken,
  opnieuwDoen,
  type Historie,
  type ProjectModel,
} from '@video-studio/shared';
import { api, ApiFoutException, type MediaDto, type ProjectDto } from '../api/client';

/**
 * De centrale editorstate.
 *
 * Eén hook beheert het project, de undo-geschiedenis, de selectie en het
 * opslaan. Alle bewerkingen lopen via `bewerk`, zodat elke wijziging
 * automatisch in de geschiedenis terechtkomt en de auto-save wordt gepland.
 */

export type OpslaStatus = 'opgeslagen' | 'gewijzigd' | 'bezig' | 'fout' | 'conflict';

export interface EditorState {
  project: ProjectModel | null;
  versie: number;
  historie: Historie | null;
  selectie: string | null;
  opslaStatus: OpslaStatus;
  opslaFout: string | null;
  laatsteOpgeslagen: number | null;
  mediaMap: Map<string, MediaDto>;
  media: MediaDto[];
  laden: boolean;
  fout: string | null;
  /** Afgeleide projectduur in frames; nooit apart opgeslagen. */
  duurInFrames: number;
}

export interface EditorActies {
  laadProject: (id: string) => Promise<void>;
  bewerk: (fn: (project: ProjectModel) => ProjectModel, beschrijving: string) => void;
  overschrijf: (project: ProjectModel, beschrijving: string) => void;
  undo: () => void;
  redo: () => void;
  selecteer: (itemId: string | null) => void;
  bewaarNu: (forceer?: boolean) => Promise<void>;
  herlaadMedia: () => Promise<void>;
  herlaadProject: () => Promise<void>;
}

export function useEditor(projectId: string | null): EditorState & EditorActies {
  const [project, setProject] = useState<ProjectModel | null>(null);
  const [versie, setVersie] = useState(1);
  const [historie, setHistorie] = useState<Historie | null>(null);
  const [selectie, setSelectie] = useState<string | null>(null);
  const [opslaStatus, setOpslaStatus] = useState<OpslaStatus>('opgeslagen');
  const [opslaFout, setOpslaFout] = useState<string | null>(null);
  const [laatsteOpgeslagen, setLaatsteOpgeslagen] = useState<number | null>(null);
  const [media, setMedia] = useState<MediaDto[]>([]);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  // Refs zodat de auto-save-timer altijd de nieuwste waarden ziet zonder dat
  // de timer opnieuw hoeft te worden opgezet.
  const projectRef = useRef<ProjectModel | null>(null);
  const versieRef = useRef(1);
  const timerRef = useRef<number | null>(null);
  const opgeslagenRef = useRef<string>('');

  useEffect(() => {
    projectRef.current = project;
  }, [project]);
  useEffect(() => {
    versieRef.current = versie;
  }, [versie]);

  const herlaadMedia = useCallback(async () => {
    try {
      const resultaat = await api.media();
      setMedia(resultaat.items);
    } catch (f) {
      // Media is niet kritiek voor het laden van het project.
      console.warn('Media konden niet worden geladen:', f);
    }
  }, []);

  const laadProject = useCallback(
    async (id: string) => {
      setLaden(true);
      setFout(null);
      try {
        const dto: ProjectDto = await api.project(id);
        setProject(dto.project);
        projectRef.current = dto.project;
        opgeslagenRef.current = JSON.stringify(dto.project);
        setVersie(dto.versie);
        versieRef.current = dto.versie;
        setHistorie(maakHistorie(dto.project, 'Geopend'));
        setSelectie(null);
        setOpslaStatus('opgeslagen');
        setOpslaFout(null);
        setLaatsteOpgeslagen(Date.now());
        await herlaadMedia();
      } catch (f) {
        setFout(f instanceof ApiFoutException ? f.message : 'Het project kon niet worden geladen.');
      } finally {
        setLaden(false);
      }
    },
    [herlaadMedia],
  );

  const bewaarNu = useCallback(
    async (forceer = false) => {
      const huidig = projectRef.current;
      if (!huidig || !projectId) return;
      setOpslaStatus('bezig');
      setOpslaFout(null);
      try {
        const dto = await api.bewaarProject(projectId, huidig, versieRef.current, forceer);
        setVersie(dto.versie);
        versieRef.current = dto.versie;
        // De server vult standaardsporen aan en verhoogt de versie; neem die
        // versie over zonder de lokale bewerkingen te overschrijven.
        opgeslagenRef.current = JSON.stringify(huidig);
        setOpslaStatus('opgeslagen');
        setLaatsteOpgeslagen(Date.now());
      } catch (f) {
        if (f instanceof ApiFoutException && f.code === 'VERSIE_CONFLICT') {
          setOpslaStatus('conflict');
          setOpslaFout(f.message);
        } else {
          setOpslaStatus('fout');
          setOpslaFout(f instanceof Error ? f.message : 'Opslaan mislukt.');
        }
      }
    },
    [projectId],
  );

  const planAutoSave = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    // 1,5 seconde na de laatste wijziging opslaan. Kort genoeg om niets kwijt
    // te raken, lang genoeg om niet bij elke toetsaanslag te schrijven.
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void bewaarNu();
    }, 1500);
  }, [bewaarNu]);

  const bewerk = useCallback(
    (fn: (project: ProjectModel) => ProjectModel, beschrijving: string) => {
      setProject((huidig) => {
        if (!huidig) return huidig;
        const volgende = fn(kloonProject(huidig));
        projectRef.current = volgende;
        setHistorie((h) => (h ? duw(h, volgende, beschrijving) : h));
        return volgende;
      });
      setOpslaStatus('gewijzigd');
      planAutoSave();
    },
    [planAutoSave],
  );

  /** Vervangt het project in zijn geheel (bijvoorbeeld na een AI-voorstel). */
  const overschrijf = useCallback(
    (nieuw: ProjectModel, beschrijving: string) => {
      projectRef.current = nieuw;
      setProject(nieuw);
      setHistorie((h) => (h ? duw(h, nieuw, beschrijving) : maakHistorie(nieuw, beschrijving)));
      setOpslaStatus('gewijzigd');
      planAutoSave();
    },
    [planAutoSave],
  );

  const undo = useCallback(() => {
    setHistorie((h) => {
      if (!h || !kanOngedaanMaken(h)) return h;
      const volgende = ongedaanMaken(h);
      projectRef.current = volgende.heden.project;
      setProject(volgende.heden.project);
      return volgende;
    });
    setOpslaStatus('gewijzigd');
    planAutoSave();
  }, [planAutoSave]);

  const redo = useCallback(() => {
    setHistorie((h) => {
      if (!h || !kanOpnieuwDoen(h)) return h;
      const volgende = opnieuwDoen(h);
      projectRef.current = volgende.heden.project;
      setProject(volgende.heden.project);
      return volgende;
    });
    setOpslaStatus('gewijzigd');
    planAutoSave();
  }, [planAutoSave]);

  const herlaadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const dto = await api.project(projectId);
      setProject(dto.project);
      projectRef.current = dto.project;
      setVersie(dto.versie);
      versieRef.current = dto.versie;
      setHistorie(maakHistorie(dto.project, 'Herladen'));
      setOpslaStatus('opgeslagen');
      setOpslaFout(null);
      setLaatsteOpgeslagen(Date.now());
    } catch (f) {
      setFout(f instanceof Error ? f.message : 'Herladen mislukt.');
    }
  }, [projectId]);

  // Bij het sluiten van het tabblad nog één keer opslaan als er wijzigingen zijn.
  useEffect(() => {
    const waarschuw = (e: BeforeUnloadEvent) => {
      if (opslaStatus === 'gewijzigd' || opslaStatus === 'bezig') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', waarschuw);
    return () => window.removeEventListener('beforeunload', waarschuw);
  }, [opslaStatus]);

  // Periodiek de medialijst verversen zolang er inspecties lopen.
  useEffect(() => {
    const lopend = media.some((m) => m.inspectieStatus === 'wacht' || m.inspectieStatus === 'bezig');
    if (!lopend) return;
    const id = window.setTimeout(() => void herlaadMedia(), 2500);
    return () => window.clearTimeout(id);
  }, [media, herlaadMedia]);

  const mediaMap = useMemo(() => new Map(media.map((m) => [m.id, m])), [media]);

  // Duur in frames wordt afgeleid, nooit opgeslagen als losse waarheid.
  const duurInFrames = useMemo(() => (project ? berekenDuurInFrames(project) : 0), [project]);

  return {
    project: project ? { ...project, duurOverrideInFrames: project.duurOverrideInFrames } : null,
    versie,
    historie,
    selectie,
    opslaStatus,
    opslaFout,
    laatsteOpgeslagen,
    mediaMap,
    media,
    laden,
    fout,
    laadProject,
    bewerk,
    overschrijf,
    undo,
    redo,
    selecteer: setSelectie,
    bewaarNu,
    herlaadMedia,
    herlaadProject,
    duurInFrames,
  };
}
